import { c as _c } from "react/compiler-runtime";
import { useElements, useStripe } from '@stripe/react-stripe-js';
import type { StripeError, StripeExpressCheckoutElementConfirmEvent } from '@stripe/stripe-js';
import { useTranslations } from 'next-intl';
import { useCallback, useState } from 'react';
import { useDonationFormContext, useRedirectHandler } from '@/donationPages/_dependencies/helpers';
import { useMinimalErrorHandler } from '@/donationPages/_dependencies/helpers/useErrorHandler';
import { report } from '@/errorReporting';
import { ActionFormSubmitErrorTypes } from '@/form/ActionForm/useActionForm/useActionForm';
import { NextIntlKeys } from '@/i18n/types';
import type { Maybe } from '@/types';
const nonDeclineErrorCodes = ['expired_card', 'incorrect_cvc', 'processing_error'] as const;
const knownDeclineCodes = ['generic_decline', 'insufficient_funds', 'lost_card', 'stolen_card', 'fraudulent'] as const;
type DonateErrorNextIntlKeys = NextIntlKeys<'nextjs.donate.errors'>;
type NonDeclineErrorCode = (typeof nonDeclineErrorCodes)[number];
type KnownDeclineCode = (typeof knownDeclineCodes)[number];
function isNonDeclineErrorCode(code: string | undefined): code is NonDeclineErrorCode {
  if (!code) return false;
  return nonDeclineErrorCodes.includes(code as NonDeclineErrorCode);
}
function isKnownDeclineCode(code: string | undefined): code is KnownDeclineCode {
  if (!code) return false;
  return knownDeclineCodes.includes(code as KnownDeclineCode);
}
function getCardErrorMessageKey(error: StripeError): DonateErrorNextIntlKeys {
  if (error.code === 'card_declined' && isKnownDeclineCode(error.decline_code)) {
    return `stripe_native_payment_card_declined_${error.decline_code}`;
  }
  if (error.code && isNonDeclineErrorCode(error.code)) {
    return `stripe_native_payment_${error.code}`;
  }
  return 'unknown_error';
}
export function useNormalizePaymentError() {
  const $ = _c(2);
  const t = useTranslations("nextjs.donate.errors");
  let t0;
  if ($[0] !== t) {
    t0 = error => {
      switch (error.type) {
        case "card_error":
          {
            const errorMessageKey = getCardErrorMessageKey(error);
            return t(errorMessageKey);
          }
        case "rate_limit_error":
        case "api_connection_error":
        case "api_error":
          {
            return t("try_again_later_or_different_payment_method");
          }
        case "authentication_error":
        case "invalid_request_error":
        case "idempotency_error":
          {
            return t("try_again_later_or_different_payment_method");
          }
        case "validation_error":
          {
            return t("unknown_error");
          }
        default:
          {
            return t("unknown_error");
          }
      }
    };
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const normalizePaymentError = t0;
  return normalizePaymentError;
}
function useFormSubmit() {
  const {
    submit,
    getValues,
    setBusy
  } = useDonationFormContext();
  const stripe = useStripe();
  const elements = useElements();
  const t = useTranslations('nextjs.donate.errors');
  const [displayError, setDisplayError] = useState<string | undefined>(undefined);
  const normalizePaymentError = useNormalizePaymentError();
  const redirectHandler = useRedirectHandler();
  const errorHandler = useMinimalErrorHandler();
  const onConfirm = useCallback(async (_: StripeExpressCheckoutElementConfirmEvent) => {
    if (!stripe || !elements) return;
    const handleError = (error: StripeError, errorMessage?: string) => {
      setDisplayError(errorMessage ?? t('try_again_later_or_different_payment_method'));
      report(submitError);
      const values = getValues();
      errorHandler({
        receiverId: values.receiver_id,
        receiverType: values.receiver_type,
        channel: values.channel,
        showCodonation: values.show_codonation,
        setBusy,
        errors: {
          stripe: error.message
        }
      });
    };
    const {
      error: submitError
    } = await elements.submit();
    if (submitError) {
      handleError(submitError);
      return;
    }
    const {
      paymentMethod,
      error: paymentMethodError
    } = await stripe.createPaymentMethod({
      elements
    });
    if (paymentMethodError) {
      handleError(paymentMethodError);
      return;
    }
    try {
      var {
        data
      } = await submit();
    } catch (error_) {
      const error_0 = error_ as Maybe<Error>;
      if (error_0?.message === ActionFormSubmitErrorTypes.ValidationError) return;
      setDisplayError(t('try_again_later_or_different_payment_method'));
      report(error_0);
      return;
    }
    setBusy(true);
    const {
      redirectTo,
      donationId,
      donationToken,
      stripePaymentIntentClientSecret
    } = data;
    if (!stripePaymentIntentClientSecret || !redirectTo || !donationId || !donationToken) return;
    const {
      error: error_1
    } = await stripe.confirmPayment({
      clientSecret: stripePaymentIntentClientSecret,
      confirmParams: {
        payment_method: paymentMethod.id
      },
      redirect: 'if_required'
    });
    if (error_1 && error_1.type) {
      const message = normalizePaymentError(error_1);
      handleError(error_1, message);
      return;
    }
    await redirectHandler(redirectTo, donationId, getValues());
  }, [stripe, elements, redirectHandler, getValues, t, errorHandler, setBusy, submit, normalizePaymentError]);
  return {
    onConfirm,
    displayError
  };
}
export default useFormSubmit;